<script setup lang="ts">
  import ModalDialog from '@/VueComponents/Modals/ModalDialog.vue';
  import resourceHelper from '@/Utils/resourceHelper';
  import { UploadOption } from '@/Types/Enums/uploadOption';
  import FileSizeFormatter from '@/Utils/fileSizeFormatter';
  import { UploadContentType } from '@/Types/Enums/uploadContentType';
  import useResetableState from '@/VueCore/utils/useResetableState';

  const props = defineProps<{
    id?: string,
    uploadOption?: string,
    fileList?: File[],
    foldersCount?: number | undefined
  }>();


  const emit = defineEmits<{ 'cancel', 'upload' }>();

  const visible = defineModel<boolean>({ default: false });
  const { state, resetState } = useResetableState(() => {

    // if the modal is hidden return empty state object
    if (!visible.value) {
      return {} as any;
    }

    const modalState = {
      selectedFileFolder: (props.uploadOption === UploadOption.selectFolder || props.uploadOption === UploadOption.selectFiles),
      selectedZip: props.uploadOption === UploadOption.selectZipFiles,
      uploadModalTitle: '',
      typeOfZipContent: '',
      generalContentZipDescription: '',
      deviceBackupDescription: '',
      cameraSystemExportDescription: '',
      generalContentDescription: '',
      typeOfFolderContent: '',
      radioCheck: UploadContentType.generalContent
    };

    if (modalState.selectedFileFolder) {
      if (props.foldersCount && props.foldersCount > 1) {
        modalState.uploadModalTitle = resourceHelper.getString('UploadFoldersTitle', {
          foldersCount: props.foldersCount, filesCount: props.fileList.length });
        modalState.typeOfFolderContent = resourceHelper.getString('TypeOfFoldersContent');
        modalState.generalContentDescription = resourceHelper.getString('GeneralContentFoldersDescription');
        modalState.deviceBackupDescription = resourceHelper.getString('DeviceBackupsDescription');
        modalState.cameraSystemExportDescription = resourceHelper.getString('CameraSystemExportsDescription');
      } else {
        modalState.uploadModalTitle = resourceHelper.getString('UploadFolderTitle', { count: props.fileList.length });
        modalState.typeOfFolderContent = resourceHelper.getString('TypeOfFolderContent');
        modalState.generalContentDescription = resourceHelper.getString('GeneralContentDescription');
        modalState.deviceBackupDescription = resourceHelper.getString('DeviceBackupDescription');
        modalState.cameraSystemExportDescription = resourceHelper.getString('CameraSystemExportDescription');
      }
    } else {
      if (props.fileList.length > 1) {
        modalState.uploadModalTitle = resourceHelper.getString(
          'UploadFoldersTitle',
          {
            foldersCount: props.foldersCount,
            filesCount: props.fileList.length
          }
        );
        modalState.typeOfZipContent = resourceHelper.getString('TypeOfZipFilesContent');
        modalState.generalContentZipDescription = resourceHelper.getString('GeneralContentZipFilesDescription');
        modalState.deviceBackupDescription = resourceHelper.getString('DeviceBackupsDescription');
        modalState.cameraSystemExportDescription = resourceHelper.getString('CameraSystemExportsDescription');
      } else {
        modalState.uploadModalTitle = resourceHelper.getString('UploadZipFileTitle', { size: formatFileSize(totalFileSize()) });
        modalState.typeOfZipContent = resourceHelper.getString('TypeOfZipContent');
        modalState.generalContentZipDescription = resourceHelper.getString('GeneralContentZipDescription');
        modalState.deviceBackupDescription = resourceHelper.getString('DeviceBackupDescription');
        modalState.cameraSystemExportDescription = resourceHelper.getString('CameraSystemExportDescription');
      }
    }

    return modalState;
  });

  function onModalUpload() {
    emit('upload', state.radioCheck);
  }

  function totalFileSize() {
    let fileSize = 0;
    if (props.fileList !== null) {
      Object.values(props.fileList).forEach(file => {
        fileSize += file.size;
      });
    }
    return fileSize;
  }

  function formatFileSize(bytes: number): string {
    return FileSizeFormatter.getFormattedFileSize(bytes);
  }

</script>

<template>
  <ModalDialog
    v-model="visible"
    :title="state.uploadModalTitle ?? ''"
    @open="resetState"
    @close="emit('cancel')"
  >
    <template #default>
      <p v-if="state.selectedFileFolder">
        {{ state.typeOfFolderContent }}
      </p>
      <p v-if="state.selectedZip">
        {{ state.typeOfZipContent }}
      </p>
      <div class="form-check">
        <input
          :id="'generalContent-' + id"
          v-model="state.radioCheck"
          type="radio"
          name="ContentType"
          value="GeneralContent"
          class="form-check-input"
        > 
        <label
          v-if="state.selectedFileFolder"
          :for="'generalContent-' + id"
          class="form-check-label"
        >
          {{ $localize('GeneralContent') }}
          <br>
          {{ state.generalContentDescription }}
        </label>
        <label
          v-if="state.selectedZip"
          :for="'generalContent-' + id"
          class="form-check-label"
        >
          {{ $localize('GeneralContent') }}
          <br>
          {{ state.generalContentZipDescription }}
        </label>
      </div>
      <div class="form-check mt-2">
        <input
          :id="'cctv' + id"
          v-model="state.radioCheck"
          class="form-check-input"
          type="radio"
          name="ContentType"
          value="Cctv"
        > 
        <label
          :for="'cctv' + id"
          class="form-check-label"
        >
          {{ $localize('CameraSystemExport') }}
          <br>
          {{ state.cameraSystemExportDescription }}
        </label>
      </div>
      <div class="form-check mt-2">
        <input
          :id="'deviceBackup' + id"
          v-model="state.radioCheck"
          class="form-check-input"
          type="radio"
          name="ContentType"
          value="DeviceBackup"
        >
        <label
          :for="'deviceBackup' + id"
          class="form-check-label"
        >
          {{ $localize('DeviceBackup') }}
          <br>
          {{ state.deviceBackupDescription }}
        </label>
      </div>
    </template>
    <template #footer>
      <button
        ref="closeBtn"
        class="btn btn-outline-secondary"
        @click="visible = false"
      >
        {{ $localize('Cancel') }}
      </button>
      <button
        ref="uploadBtn"
        class="btn btn-primary"
        @click="onModalUpload"
      >
        {{ $localize('Upload') }}
      </button>
    </template>
  </ModalDialog>
</template>

<style lang="scss" scoped>
    .contentDescription {
        margin-left: 17px;
    }
</style>